import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    VStack,
    HStack,
    Card,
    CardBody,
    CardHeader,
    Heading,
    Text,
    useColorModeValue,
    Spinner,
    Alert,
    AlertIcon,
    AlertDescription,
    Badge,
    SimpleGrid,
    Flex,
    Icon,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer
} from '@chakra-ui/react';
import {
    MdPeople,
    MdBusiness,
    MdDescription,
    MdTrendingUp,
    MdTrendingDown,
    MdCheckCircle,
    MdError,
    MdPending,
    MdAssessment
} from 'react-icons/md';
import ReactApexChart from 'react-apexcharts';
import apiService from '../services/apiService';
import { formatNumber, formatDateTime, getStatusColor } from '../utils/helpers';
import { ToastManager } from '../components/Common/ToastManager';

/**
 * Main Dashboard Page Component
 */
function Dashboard({ userProfile, appConfig }) {
    const [dashboardData, setDashboardData] = useState(null);
    const [heartbeatData, setHeartbeatData] = useState(null);
    const [reportResults, setReportResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Theme-aware colors
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const chartTheme = useColorModeValue('light', 'dark');

    // Ref for interval management
    const refreshInterval = useRef(null);

    /**
     * Initialize dashboard and start auto-refresh
     * Sets up data loading and periodic updates
     */
    useEffect(() => {
        loadDashboardData();
        setupAutoRefresh();
        
        return cleanup;
    }, []);

    /**
     * Setup auto-refresh for dashboard data
     * Refreshes data every 15 seconds as per requirements
     */
    const setupAutoRefresh = () => {
        const interval = appConfig?.app?.features?.autoRefresh?.dashboard || 15000;
        refreshInterval.current = setInterval(loadDashboardData, interval);
    };

    /**
     * Cleanup intervals on unmount
     * Prevents memory leaks and unnecessary API calls
     */
    const cleanup = () => {
        if (refreshInterval.current) {
            clearInterval(refreshInterval.current);
            refreshInterval.current = null;
        }
    };

    /**
     * Load dashboard statistics, heartbeat data, and report results
     * Enhanced to include report results from analytics
     */
    const loadDashboardData = async () => {
        try {
            setError(null);
            
            // Load dashboard statistics, heartbeat data, and report results
            const [statsResponse, heartbeatResponse, reportResultsResponse] = await Promise.all([
                apiService.getDashboardStats(),
                apiService.getHeartbeatStatus(),
                apiService.getReportResults()
            ]);

            // Update state with fetched data
            if (statsResponse.success) {
                setDashboardData(statsResponse.data);
            }

            if (heartbeatResponse.success) {
                setHeartbeatData(heartbeatResponse.data);
            }

            if (reportResultsResponse.success) {
                setReportResults(reportResultsResponse.data);
            }

            setLastUpdated(new Date());

        } catch (error) {
            setError(error.message || 'Failed to load dashboard data');
            
            ToastManager.showError('Failed to refresh dashboard data');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Stat Card Component
     * Displays individual statistics with icons
     */
    const StatCard = ({ title, value, icon, color = 'blue' }) => (
        <Card 
            bg={cardBg} 
            borderColor={borderColor}
            transition="all 0.2s"
        >
            <CardBody>
                <Flex justify="space-between" align="start">
                    <Box>
                        <Text fontSize="sm" color={textColor} mb={2}>
                            {title}
                        </Text>
                        <Text fontSize="2xl" fontWeight="bold" color={headingColor}>
                            {formatNumber(value)}
                        </Text>
                    </Box>
                    <Icon as={icon} w={8} h={8} color={`${color}.500`} />
                </Flex>
            </CardBody>
        </Card>
    );

    /**
     * Timeline Chart Component
     * Displays time-based data using ApexCharts
     */
    const TimelineChart = ({ title, data, height = 300 }) => {
        // Transform timeline data for chart
        const chartData = data && data.length > 0 ? data.map(item => ({
            x: item.date,
            y: item.count
        })) : [];
        
        const chartOptions = {
            chart: {
                type: 'line',
                height: height,
                toolbar: { show: false },
                background: 'transparent'
            },
            stroke: {
                curve: 'smooth',
                width: 3
            },
            colors: ['#4318ff'],
            xaxis: {
                type: 'datetime',
                labels: {
                    style: {
                        colors: chartTheme === 'dark' ? '#cbd5e0' : '#4a5568'
                    }
                }
            },
            yaxis: {
                labels: {
                    style: {
                        colors: chartTheme === 'dark' ? '#cbd5e0' : '#4a5568'
                    }
                }
            },
            grid: {
                borderColor: borderColor,
                strokeDashArray: 5
            },
            theme: {
                mode: chartTheme
            },
            tooltip: {
                theme: chartTheme
            }
        };

        const chartSeries = [{
            name: title,
            data: chartData
        }];

        return (
            <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                    <Heading size="md" color={headingColor}>
                        {title}
                    </Heading>
                </CardHeader>
                <CardBody>
                    <ReactApexChart
                        options={chartOptions}
                        series={chartSeries}
                        type="line"
                        height={height}
                    />
                </CardBody>
            </Card>
        );
    };

    /**
     * Render loading state
     * Shows loading spinner while data is being fetched
     */
    if (loading) {
        return (
            <Flex justify="center" align="center" minH="400px">
                <VStack spacing={4}>
                    <Spinner size="xl" color="brand.500" thickness="4px" />
                    <Text color={textColor}>Loading dashboard...</Text>
                </VStack>
            </Flex>
        );
    }

    /**
     * Render error state
     * Shows error message with retry option
     */
    if (error) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                    {error}. Dashboard will retry automatically.
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <Box>
            {/* Page Header */}
            <VStack spacing={6} align="stretch">
                <Box>
                    <Heading size="lg" color={headingColor} mb={2}>
                        Dashboard Overview
                    </Heading>
                    <HStack spacing={4} color={textColor} fontSize="sm">
                        <Text>Welcome back, {userProfile?.name || 'Administrator'}</Text>
                        {lastUpdated && (
                            <Badge colorScheme="green" variant="subtle">
                                Last updated: {formatDateTime(lastUpdated, { showDate: false })}
                            </Badge>
                        )}
                    </HStack>
                </Box>

                {/* Section 1: Client Statistics */}
                <Box>
                    <Heading size="md" color={headingColor} mb={4}>
                        Client Analytics
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                        <StatCard
                            title="Total Clients"
                            value={heartbeatData?.clients?.length || 0}
                            icon={MdBusiness}
                            color="purple"
                        />
                        <StatCard
                            title="Online Clients"
                            value={heartbeatData?.summary?.online || 0}
                            icon={MdCheckCircle}
                            color="green"
                        />
                        <StatCard
                            title="Offline Clients"
                            value={heartbeatData?.summary?.offline || 0}
                            icon={MdError}
                            color="red"
                        />
                        <StatCard
                            title="Clients Not Mapped to Report"
                            value={dashboardData?.clients?.notMappedToReport || 0}
                            icon={MdError}
                            color="orange"
                        />
                    </SimpleGrid>
                </Box>

                {/* Client Creation Timeline */}
                {dashboardData?.clientTimeline && dashboardData.clientTimeline.length > 0 && (
                    <TimelineChart
                        title="Client Registration Timeline"
                        data={dashboardData.clientTimeline}
                    />
                )}

                {/* Section 2: Report Statistics */}
                <Box>
                    <Heading size="md" color={headingColor} mb={4}>
                        Report Analytics
                    </Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 6 }} spacing={6}>
                        <StatCard
                            title="Total Reports"
                            value={dashboardData?.reports?.total || 0}
                            icon={MdDescription}
                            color="indigo"
                        />
                        <StatCard
                            title="Enabled Reports"
                            value={dashboardData?.reports?.enabled || 0}
                            icon={MdCheckCircle}
                            color="green"
                        />
                        <StatCard
                            title="Disabled Reports"
                            value={dashboardData?.reports?.disabled || 0}
                            icon={MdError}
                            color="gray"
                        />
                        <StatCard
                            title="Cron Enabled"
                            value={dashboardData?.reports?.cronEnabled || 0}
                            icon={MdTrendingUp}
                            color="blue"
                        />
                        <StatCard
                            title="Cron Disabled"
                            value={dashboardData?.reports?.cronDisabled || 0}
                            icon={MdTrendingDown}
                            color="orange"
                        />
                        <StatCard
                            title="Unmapped Reports"
                            value={dashboardData?.reports?.notMappedToClient || 0}
                            icon={MdError}
                            color="red"
                        />
                    </SimpleGrid>
                </Box>

                {/* Report Creation Timeline */}
                {dashboardData?.reportTimeline && dashboardData.reportTimeline.length > 0 && (
                    <TimelineChart
                        title="Report Creation Timeline"
                        data={dashboardData.reportTimeline}
                    />
                )}

                {/* Section 3: Report Results (Moved from Analytics) */}
                <Box>
                    <Heading size="md" color={headingColor} mb={4}>
                        Report Results
                    </Heading>
                    
                    {/* Report Results Summary Stats */}
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
                        <StatCard
                            title="Total Results"
                            value={reportResults?.summary?.total || 0}
                            icon={MdAssessment}
                            color="blue"
                        />
                        <StatCard
                            title="Successful Executions"
                            value={reportResults?.summary?.success || 0}
                            icon={MdCheckCircle}
                            color="green"
                        />
                        <StatCard
                            title="Failed Executions"
                            value={reportResults?.summary?.error || 0}
                            icon={MdError}
                            color="red"
                        />
                    </SimpleGrid>

                    {/* Report Results Table */}
                    {reportResults?.results && reportResults.results.length > 0 && (
                        <Card bg={cardBg} borderColor={borderColor}>
                            <CardHeader>
                                <Heading size="sm">Recent Execution Results</Heading>
                            </CardHeader>
                            <CardBody p={0}>
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th>Client Name</Th>
                                                <Th>Report Code</Th>
                                                <Th>Report Description</Th>
                                                <Th>Status</Th>
                                                <Th>Executed At</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {reportResults.results.map((result, index) => (
                                                <Tr key={index}>
                                                    <Td fontWeight="medium">{result.clientName}</Td>
                                                    <Td>
                                                        <Badge variant="outline" colorScheme="gray">
                                                            {result.reportCode}
                                                        </Badge>
                                                    </Td>
                                                    <Td>{result.reportDescription}</Td>
                                                    <Td>
                                                        <Badge
                                                            colorScheme={getStatusColor(result.executionStatus)}
                                                            variant="subtle"
                                                        >
                                                            {result.executionStatus}
                                                        </Badge>
                                                    </Td>
                                                    <Td fontSize="sm" color={textColor}>
                                                        {formatDateTime(result.executedAtUtc)}
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            </CardBody>
                        </Card>
                    )}
                </Box>
            </VStack>
        </Box>
    );
}

export default Dashboard;